import React from 'react';
import { Segmented } from 'antd';
import { ResumeTemplateId, ResumeTheme } from '../../types';
import css from './ThemeBar.module.scss';

interface ThemeBarProps {
  templateId: ResumeTemplateId;
  theme: ResumeTheme;
  /** Partial change: the page PATCHes exactly what moved. Accent/density
   *  clicks emit the FULL merged snake_case theme (the API replaces the
   *  whole theme object). */
  onChange: (partial: { templateId?: ResumeTemplateId; theme?: ResumeTheme }) => void;
  /** True while the page has any mutation in flight — disables every control
   *  so a theme PATCH can't race another request's version update. */
  busy?: boolean;
}

// Local swatch colors — presentation only; the backend templates own the
// actual rendered accents.
const ACCENTS: Array<{
  accent: ResumeTheme['accent'];
  color: string;
  label: string;
}> = [
  { accent: 'green', color: '#207551', label: 'Green accent' },
  { accent: 'navy', color: '#1e3a5f', label: 'Navy accent' },
  { accent: 'burgundy', color: '#6b2737', label: 'Burgundy accent' },
  { accent: 'slate', color: '#475569', label: 'Slate accent' },
  { accent: 'none', color: '#0b081c', label: 'No accent' },
];

/**
 * Compact appearance bar under the preview: template, accent, density.
 *
 * Deliberately NO photo switch in v1: there is no photo upload path yet, so
 * the toggle would be a dead control. `ResumeVersion.photoAllowed` (and
 * `theme.photo_url`) are reserved for when upload lands — render the switch
 * here, gated on `photoAllowed`, with the market-aware coach tooltip.
 */
const ThemeBar: React.FC<ThemeBarProps> = ({
  templateId,
  theme,
  onChange,
  busy = false,
}) => (
  <div className={css.bar}>
    <Segmented
      options={[
        { label: 'Classic', value: 'classic' },
        { label: 'Modern', value: 'modern' },
      ]}
      value={templateId}
      disabled={busy}
      onChange={(value) => onChange({ templateId: value as ResumeTemplateId })}
      aria-label="Resume template"
    />

    <div className={css.accents} role="group" aria-label="Accent color">
      {ACCENTS.map(({ accent, color, label }) => (
        <button
          key={accent}
          type="button"
          className={`${css.dot} ${theme.accent === accent ? css.dotSelected : ''}`}
          style={{ backgroundColor: color }}
          aria-label={label}
          aria-pressed={theme.accent === accent}
          disabled={busy}
          onClick={() => {
            // Same-value clicks are a no-op (Segmented already behaves this
            // way) — never PATCH a theme that didn't change.
            if (theme.accent !== accent) onChange({ theme: { ...theme, accent } });
          }}
        />
      ))}
    </div>

    <Segmented
      options={[
        { label: 'Airy', value: 'airy' },
        { label: 'Compact', value: 'compact' },
      ]}
      value={theme.density}
      disabled={busy}
      onChange={(value) =>
        onChange({
          theme: { ...theme, density: value as ResumeTheme['density'] },
        })
      }
      aria-label="Layout density"
    />
  </div>
);

export default ThemeBar;
